<template>
  <div class="c-related__component">
    <div
      v-if="isLoading"
      class="c-loading__container u-mbm"
    >
      <SVGIcon class="loading-icon" width="80" height="80" name="loading" />
    </div>
    <template v-else>
      <template v-if="relatedContents && relatedContents.length">
        <div class="c-related__container">
          <h3 class="c-related__title">{{ t('relatedContent') }}</h3>
          <div class="c-related__carousel-container">
            <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation">
              <button
                type="button"
                :class="currentSlideIndex === 0 ? 'carousel__navigation-button--disabled' : ''"
                class="carousel__navigation-button u-mrs"
                :disabled="currentSlideIndex === 0"
                @click="splideCarousel.go('<')"
              >
                <SVGIcon class="c-button__icon" name="arrow-left-white" />
              </button>
            </div>
            <Splide ref="splideCarousel" :has-track="false" :options="{ rewind: false, perPage: maxSlidesPerPage, gap: '1rem', breakpoints: breakpoints, arrows: false, pagination: false }" aria-label="Carousel">
              <SplideTrack>
                <SplideSlide
                  v-for="content in relatedContents"
                  :key="content.data.pk"
                >
                  <a v-if="content.data" class="c-related__card-link c-carousel__card-link" :href="parseContentUrl(content.type, content.data.slug)">
                    <div class="carousel__item">
                      <template v-if="content.data.images">
                        <template v-if="content.data.images[0]">
                          <img
                            :class="content.data.images[0].thumbnail_368_256_crop && urlExists(content.data.images[0].thumbnail_368_256_crop) ? '' : 'c-related__card-thumbnail-placeholder'"
                            class="c-related__card-thumbnail"
                            :src="content.data.images[0].thumbnail_368_256_crop && urlExists(content.data.images[0].thumbnail_368_256_crop) ? content.data.images[0].thumbnail_368_256_crop : baseUrl + 'icons/logo-forum-black.svg'"
                            alt="content thumbnail"
                          >
                        </template>
                        <img
                          v-else
                          class="c-related__card-thumbnail c-related__card-thumbnail-placeholder"
                          :src="baseUrl + 'icons/logo-forum-black.svg'"
                          alt="content thumbnail"
                        >
                      </template>
                      <img
                        v-else
                        class="c-related__card-thumbnail c-related__card-thumbnail-placeholder"
                        :src="baseUrl + 'icons/logo-forum-black.svg'"
                        alt="content thumbnail"
                      >
                      <div class="u-flex">
                        <div :class="'related-' + content.type.toLowerCase()" class="c-card__text c-related__card-content">
                          <p>{{ content.type.charAt(0).toUpperCase() + content.type.slice(1) }}</p>
                        </div>
                        <template v-if="content.type.toLowerCase() === 'project' && content.data.is_premium">
                          <div class="c-card__project-premium">
                            <img class="c-card__icon c-card__premium-icon" :src="baseUrl + 'icons/diamond-small.svg'" aria-hidden="true" alt="diamond icon">
                            <label>Premium</label>
                          </div>
                        </template>
                      </div>
                      <h3 class="c-related__card-title">{{ truncateString(content.data.title, 100) }}</h3>
                      <div class="c-card__text c-related__card-description">
                        {{ truncateString(content.type.toLowerCase() === 'project' ? filterDescriptionBanner(content.data.description) : content.data.description, 130) }}
                      </div>
                      <div v-if="content.type.toLowerCase() === 'event'" class="c-related__card-date">
                        <!-- if event is ending the same day -->
                        <template v-if="content.data.end && (dateFromISO(content.data.start) === dateFromISO(content.data.end))">
                          <ul v-if="content.data.start" class="c-related__card-metas">
                            <li class="c-related__card-meta">
                              <SVGIcon class="c-related__card-icon" name="calendar" />
                              <span class="c-related__card-label">{{ dateFromISO(content.data.start) }}</span>
                            </li>
                            <li class="c-related__card-meta">
                              <SVGIcon class="c-related__card-icon" name="time" />
                              <span class="c-related__card-label">{{ timeFromISO(content.data.start) }}</span>
                              <span v-if="content.data.end" class="c-related__card-label"> - {{ timeFromISO(content.data.end) }}</span>
                            </li>
                          </ul>
                        </template>
                        <!-- else if event is ending on different day -->
                        <ul v-else class="c-related__card-metas">
                          <li v-if="content.data.start" class="c-related__card-meta">
                            <SVGIcon class="c-related__card-icon" name="calendar" />
                            <span class="c-related__card-label">{{ dateFromISO(content.data.start) }}</span>
                            <span v-if="content.data.end" class="c-related__card-label"> - {{ dateFromISO(content.data.end) }}</span>
                          </li>
                          <li v-if="content.data.start" class="c-related__card-meta u-mtxs">
                            <SVGIcon class="c-related__card-icon" name="time" />
                            <span class="c-related__card-label">{{ timeFromISO(content.data.start) }}</span>
                            <span v-if="content.data.end" class="c-related__card-label u-mlxs">- {{ timeFromISO(content.data.end) }}</span>
                          </li>
                        </ul>
                      </div>
                      <template v-if="content.data.topics">
                        <ul v-if="content.data.topics.length" class="c-related__card-topics">
                          <template v-for="(topic, index) in content.data.topics">
                            <a
                              v-if="index < topicsMax"
                              :key="topic.id"
                              :href="'/search/?topics=' + topic.name" class="c-card__topic-link">
                              <li class="c-related__card-topic">
                                <p>{{ truncateString(topic.name, 15) }}</p>
                              </li>
                            </a>
                          </template>
                        </ul>
                      </template>
                      <div class="c-related__card-footer">
                        <div v-if="content.type !== 'Project'" class="c-related__card-avatar">
                          <template v-if="content.author">
                            <!-- content author avatar -->
                            <template v-if="content.author.forum_user.avatar_url && urlExists(content.author.forum_user.avatar_url)">
                              <a :href="'/profile/' + content.author.username + '/'" class="c-related__avatar-link">
                                <SVGIcon class="c-related__avatar-border" width="32" height="32" name="avatar-outline-thick" />
                                <img
                                  :src="content.author.forum_user.avatar_url"
                                  alt=""
                                  class="c-related__avatar-image"
                                >
                                <SVGIcon v-if="content.author.forum_user.is_premium" class="c-related__avatar-premium-icon" name="diamond-small" />
                              </a>
                            </template>
                            <template v-else>
                              <!-- if no avatar image -->
                              <a :href="'/profile/' + content.author.username + '/'" class="c-related__avatar-link">
                                <img :src="baseUrl + 'icons/avatar.svg'" alt="" class="c-related__avatar-image c-related__avatar-image-placeholder">
                                <SVGIcon v-if="content.author.forum_user.is_premium" class="c-related__avatar-premium-icon" name="diamond-small" />
                              </a>
                            </template>
                          </template>
                          <template v-else>
                            <!-- placeholder if no content author -->
                            <img
                              :src="baseUrl + 'icons/avatar.svg'"
                              alt=""
                              class="c-related__avatar-image c-related__avatar-image-placeholder"
                            >
                          </template>
                        </div>
                        <div v-else class="c-related__avatar c-related__project-contributors">
                          <ProjectContributors
                            :project-id="content.data.pk"
                            theme="card"
                            limit="4"
                          />
                        </div>
                        <div class="c-related__card-star">
                          <Bookmark
                            :content-id="content.data.pk"
                            :content-name="pluralize(content.type.toLowerCase())"
                          />
                        </div>
                      </div>
                    </div>
                  </a>
                </SplideSlide>
              </SplideTrack>
              <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation carousel__navigation-bottom">
                <button
                  type="button"
                  class="carousel__navigation-button u-mrs"
                  @click="splideCarousel.go('<')"
                >
                  <SVGIcon class="c-button__icon" name="arrow-left-white" />
                </button>
                <button
                  type="button"
                  class="carousel__navigation-button"
                  @click="splideCarousel.go('>')"
                >
                  <SVGIcon class="c-button__icon" name="arrow-right-white" />
                </button>
              </div>
            </Splide>
            <div v-if="splideCarousel && splideCarousel.splide" class="carousel__navigation">
              <button
                type="button"
                :class="(currentSlideIndex + maxSlidesPerPage) >= splideCarousel.length ? 'carousel__navigation-button--disabled' : ''"
                class="carousel__navigation-button u-mls"
                :disabled="(currentSlideIndex + maxSlidesPerPage) >= splideCarousel.length"
                @click="splideCarousel.go('>')"
              >
                <SVGIcon class="c-button__icon" name="arrow-right-white" />
              </button>
            </div>
          </div>
        </div>
      </template>
      <!-- <template v-else>
        <p class="u-mts">{{ t('noResultsFound') }}</p>
      </template> -->
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import {
  getUser,
  Topic,
  TopicStatus,
  ContentType,
  Article,
  getArticle,
  getArticleAbortable,
  Event,
  getEvent,
  getEventAbortable,
  Project,
  getProject,
  getProjectLightAbortable,
  Collection,
  getCollection,
  getCollectionAbortable,
  getIdNumber,
  getFacetTopic
} from '@/utils/api'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { truncateString, pluralize, filterDescriptionBanner } from '@/utils/string'
import { parseContentUrl, urlExists } from '@/utils/urls'
import { dateFromISO, timeFromISO } from '@/utils/time'
import { countCommonKeys, getNestedArrayLength } from '@/utils/array'
// import { logDevOnly } from '@/utils/logs'
import ProjectContributors from '@/components/ProjectContributors.ce.vue'
import Bookmark from '@/components/Bookmark.ce.vue'
import SVGIcon from '@/components/SVGIcon.vue'
import { useI18n } from '@/utils/i18n'

import '@splidejs/vue-splide/css/core'

const props = defineProps<{
  pk: number
  type: string
}>()

// logDevOnly()

const isLoading = ref(true)

const baseUrl = '/static/'

const topics = ref()
const topicsMax = ref(5)
const topicSearchResultMax = ref(4)
const commonTopicsMin = ref(2)

const relatedContents = ref<any[]>([])
const nestedRelatedContentMax = ref(5)
const totalRelatedContentMax = ref(20)

const splideCarousel = ref()
const currentSlideIndex = ref(0)
const maxSlidesPerPage = ref(4)

const breakpoints = {
  300: {
    perPage: 1,
    gap: '1rem'
  },
  800: {
    perPage: 1,
    gap: '1rem'
  },
  990: {
    perPage: 2,
    gap: '1rem'
  },
  1240: {
    perPage: 3,
    gap: '1rem'
  },
  1300: {
    perPage: 4,
    gap: '1rem'
  }
}

function setMaxSlidesBreakpointsOnMount () {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth

  if ((viewportWidth >= 300) && (viewportWidth < 800)) {
    maxSlidesPerPage.value = breakpoints[300].perPage
  }

  if ((viewportWidth >= 800) && (viewportWidth < 990)) {
    maxSlidesPerPage.value = breakpoints[800].perPage
  }

  if ((viewportWidth >= 990) && (viewportWidth < 1240)) {
    maxSlidesPerPage.value = breakpoints[990].perPage
  }

  if ((viewportWidth >= 1240) && (viewportWidth < 1300)) {
    maxSlidesPerPage.value = breakpoints[1240].perPage
  }

  if ((viewportWidth >= 1300)) {
    maxSlidesPerPage.value = breakpoints[1300].perPage
  }
}

watch([ splideCarousel ], async () => {
  if (splideCarousel.value) {
    // add carousel event listener
    splideCarousel.value.splide.on('moved', function (newIndex: number) {
      currentSlideIndex.value = newIndex
    })
  }
}, { immediate: true })

async function parseTopics (): Promise<any> {
  const pk = ref(props.pk)
  const contentType = ref(props.type)

  const content = ref()

  // Article
  if (contentType.value === 'article') {
    try {
      content.value = await getArticle(pk.value)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      throw err
    }
  }

  // Event
  if (contentType.value === 'event') {
    try {
      content.value = await getEvent(pk.value)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      throw err
    }
  }

  // Project
  if (contentType.value === 'project') {
    try {
      content.value = await getProject(pk.value)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      throw err
    }
  }

  // Collection
  if (contentType.value === 'collection') {
    try {
      content.value = await getCollection(pk.value)
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        return
      }
      throw err
    }
  }

  const topics = ref(content.value.topics)
  const filteredTopics = ref()

  if (topics.value) {
    if (topics.value.length) {
      filteredTopics.value = ref(Array.from(topics.value).filter((topic: any) => {
        return topic.status === TopicStatus.accepted
      }))

      if (filteredTopics.value.length) {
        // get first n topics in filtered topics
        filteredTopics.value = filteredTopics.value.splice(0, topicsMax.value)
      }
    }
  }

  console.log('FILTERED TOPICS: ', filteredTopics.value)

  return filteredTopics.value
}

let controller: AbortController | null = null

interface ContentSearchResult {
  id: number
  topic: string
  type: ContentType
  data: Article | Event | Project | Collection
}

// id array to keep track of already returned content
const contentIDs = ref<Array<string>>([])

async function getRelatedContentFromTopic (topic: Topic): Promise<any[]> {
  const contentResults = ref<ContentSearchResult[]>([])

  const topicName = topic.name

  const searchResults = ref()
  searchResults.value = await getFacetTopic(topicName)
  if (searchResults.value > topicSearchResultMax.value) {
    // get first n search results from topic search
    searchResults.value = searchResults.value.splice(0, topicSearchResultMax.value)
  }
  // console.log('SEARCH RESULT: ', searchResults.value)

  controller = new AbortController()

  for (let i = 0; i < searchResults.value.length; i++) {
    // prevent duplicate content in results
    const hasID = contentIDs.value.some(content => content === searchResults.value[i].id)
    if (!hasID) {
      contentIDs.value.push(searchResults.value[i].id)

      const id = getIdNumber(searchResults.value[i].id)

      // Article
      if (searchResults.value[i].id.includes('ircamforum.forumarticle')) {
        try {
          const data = await getArticleAbortable(id, controller)
          if (data.status === 2) {
            const articleResult = {
              id: id,
              topic: topicName,
              type: ContentType.Article,
              author: data!.user ? await getUser(data!.user as any) : null,
              data: data
            }
            contentResults.value.push(articleResult)
          }
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') {
            return []
          }
          throw err
        }
      }

      // Guest Article
      if (searchResults.value[i].id.includes('ircamforum.guestarticle')) {
        try {
          const data = await getArticleAbortable(id, controller)

          // check if guest article share at least n topics in common
          const topicsIds = ref<any[]>([])
          for (let i = 0; i < topics.value.length; i++) {
            topicsIds.value.push(topics.value[i].id)
          }

          const guestArticleTopicsIds = ref<any[]>([])
          for (let i = 0; i < data.topics.length; i++) {
            guestArticleTopicsIds.value.push(data.topics[i].id)
          }

          const commonTopicsLength = countCommonKeys(topicsIds.value, guestArticleTopicsIds.value)

          if (commonTopicsLength >= commonTopicsMin.value) {
            if (data.status === 2) {
              const guestArticleResult = {
                id: id,
                topic: topicName,
                type: ContentType.Article,
                author: data!.user ? await getUser(data!.user as any) : null,
                data: data
              }
              contentResults.value.push(guestArticleResult)
            }
          }
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') {
            return []
          }
          throw err
        }
      }

      // Event
      if (searchResults.value[i].id.includes('mezzanine_agenda.event')) {
        try {
          const data = await getEventAbortable(id, controller)
          if (data.status === 2) {
            const eventResult = {
              id: id,
              topic: topicName,
              type: ContentType.Event,
              author: data!.user ? await getUser(data!.user as any) : null,
              data: data
            }
            contentResults.value.push(eventResult)
          }
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') {
            return []
          }
          throw err
        }
      }

      // Project
      if (searchResults.value[i].id.includes('organization_projects.project')) {
        try {
          const data = await getProjectLightAbortable(id, controller)
          if (data.status === 'published') {
            const projectResult = {
              id: id,
              topic: topicName,
              type: ContentType.Project,
              author: data!.owner ? await getUser(data!.owner.pk) : null,
              data: data
            }
            contentResults.value.push(projectResult)
          }
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') {
            return []
          }
          throw err
        }
      }

      // Collection
      if (searchResults.value[i].id.includes('ircamforum.collection')) {
        try {
          const data = await getCollectionAbortable(id, controller)
          const collectionResult = {
            id: id,
            topic: topicName,
            type: ContentType.Collection,
            author: data!.user ? await getUser(data!.user as any) : null,
            data: data
          }
          contentResults.value.push(collectionResult)
        } catch (err: unknown) {
          if (err instanceof Error && err.name === 'AbortError') {
            return []
          }
          throw err
        }
      }
    }
  }

  return contentResults.value
}

async function getTopicsRelatedContent (): Promise<void> {
  // get content for each topic
  const tempArr = ref<any[]>([])
  for (let i = 0; i < topics.value.length; i++) {
    const tempContent = await getRelatedContentFromTopic(topics.value[i])
    if (tempContent) {
      tempArr.value.push(tempContent)
    }
  }

  // then parse related content as previous results are nested arrays
  const relatedContentIDs = ref<Array<string>>([])

  const tempArrTotalLength = getNestedArrayLength(tempArr.value)
  const relatedContentLenght = tempArrTotalLength <= totalRelatedContentMax.value ? tempArrTotalLength : totalRelatedContentMax.value

  if (relatedContentLenght) {
    for (let i = 0; i < relatedContentLenght; i++) {
      if (tempArr.value[i]) {
        const topicSearchResultsLength = ref(tempArr.value[i].length)

        // limit first n topic search results
        if (tempArr.value[i].length >= nestedRelatedContentMax.value) {
          topicSearchResultsLength.value = nestedRelatedContentMax.value
        }

        for (let j = 0; j < topicSearchResultsLength.value; j++) {
          const hasID = relatedContentIDs.value.some(content => content === tempArr.value[i][j].id)

          if (!hasID && (String(props.pk) !== String(tempArr.value[i][j].id))) {
            // keep track of related content ids
            relatedContentIDs.value.push(tempArr.value[i][j].id)

            // add to related content
            relatedContents.value.push(tempArr.value[i][j])
          }
        }
      }
    }
  }

  console.log('RELATED CONTENT: ', relatedContents.value)
}

onMounted(async () => {
  setMaxSlidesBreakpointsOnMount()

  // parse topics
  topics.value = await parseTopics()
  console.log('TOPICS: ', topics.value)

  if (topics.value) {
    if (topics.value.length) {
      // get related content based on topics
      await getTopicsRelatedContent()
    }
  }

  isLoading.value = false
})

const { t } = useI18n({
  messages: {
    en: {
      relatedContent: 'Related content',
      noResultsFound: 'No results found.'
    },
    fr: {
      relatedContent: 'Contenu lié',
      noResultsFound: 'Aucun résultat trouvé.'
    }
  }
})
</script>

<!-- <style scoped lang="scss">
</style> -->
